import { useState, useRef, useLayoutEffect, useMemo, useCallback } from "react";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { useFormatter } from "../../../../utils/useFormatter";
import { useDatetime } from "../../../../utils/useDatetime";
import { ChevronLeft, ChevronRight, WhatsApp } from "@mui/icons-material";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { currentFeatures, oids } from "@/configs/Settings";
import {
  SendWhatsAppMessageDialog,
  useMessagingTemplatesForSend,
} from "src-next/modules/crm-clinical/messaging";
import type {
  MessagingAppointment,
  MessagingPatient,
} from "src-next/modules/crm-clinical/messaging";

export function Header() {
  const { formatNameDisplay, formatGender, formatAge } = useFormatter;
  const { formatDate } = useDatetime;
  const [activeStep, setActiveStep] = useState(0);

  // Guarda a chave do campo cujo valor foi expandido ao clicar no "...".
  // Permite mostrar o texto completo quebrando em várias linhas.
  const [expandedFieldKey, setExpandedFieldKey] = useState<string | null>(null);

  // Referência e largura do container dos campos, usadas para calcular
  // dinamicamente quantos campos cabem em UMA linha (paginação por largura).
  const fieldsContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Header agora exibe apenas os dados do paciente.
  // Os botões de ação (Luxor, Sumário, Questionários, Teleatendimento)
  // foram movidos para o componente ActionBar.
  const { patient, appointment, tutorData } = useMedicalCareContext();
  const { organizationId } = useOrganizationContext();
  const { user } = useSessionContext();
  const [isSendWhatsAppDialogOpen, setIsSendWhatsAppDialogOpen] =
    useState(false);

  const { templates: messagingTemplates } = useMessagingTemplatesForSend({
    organizationId,
  });

  const isVet = !currentFeatures.defaultApp;
  const patientPhone = patient?.telecom?.find(
    (e: fhir5.ContactPoint) => e.system === "phone",
  )?.value;
  const tutorPhone = tutorData?.telecom?.find(
    (e: fhir5.ContactPoint) => e.system === "phone",
  )?.value;
  const whatsappPhone = isVet ? tutorPhone : patientPhone;

  const messagingAppointment: MessagingAppointment = useMemo(() => {
    return {
      id: appointment?.id ?? "appointment",
      startAtIso: appointment?.start ?? undefined,
    };
  }, [appointment?.id, appointment?.start]);

  const messagingPatient: MessagingPatient | null = useMemo(() => {
    if (!patient?.id) return null;

    const fullName = formatNameDisplay(patient?.name?.[0]?.text ?? "")
      .trim()
      .slice(0, 200);

    if (isVet) {
      const tutorCpf =
        tutorData?.id && String(tutorData.id).includes("-")
          ? String(tutorData.id).split("-")[1]
          : undefined;

      return {
        id: `tutor-of-${patient.id}`,
        fullName: fullName ? `Tutor de ${fullName}` : "Tutor",
        cpf: tutorCpf ? String(tutorCpf) : undefined,
        phone: whatsappPhone ? String(whatsappPhone) : undefined,
        isTutor: true,
      };
    }

    const cpfFromId = String(patient.id).includes("-")
      ? String(patient.id).split("-")[1]
      : "";

    return {
      id: patient.id,
      fullName: fullName || "Paciente",
      cpf: cpfFromId || undefined,
      phone: whatsappPhone ? String(whatsappPhone) : undefined,
    };
  }, [formatNameDisplay, isVet, patient?.id, patient?.name, tutorData?.id, whatsappPhone]);

  const taskPatientReference = useMemo(() => {
    return patient?.id ? `Patient/${patient.id}` : null;
  }, [patient?.id]);

  const handleOpenSendWhatsAppDialog = useCallback(() => {
    setIsSendWhatsAppDialogOpen(true);
  }, []);

  // Mede a largura do container e reage a resize/zoom para recalcular a paginação.
  useLayoutEffect(() => {
    const element = fieldsContainerRef.current;
    if (!element) return;
    const updateWidth = () => setContainerWidth(element.clientWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => Math.max(0, prevActiveStep - 1));
  };

  type DemographicDataType = {
    [key: string]: {
      label: string;
      display: any;
    };
  };

  const auxType = {
    raceExists: patient?.extension?.find(
      (el: any) =>
        el.url ===
        "http://www.saude.gov.br/fhir/StructureDefinition/BRRacaCorEtnia-1.0"
    ),
    genderExists: patient?.extension?.find(
      (el: any) =>
        el.url ===
        "http://www.saude.gov.br/fhir/StructureDefinition/BRIdentidadeGenero"
    ),
    educationExists: patient?.extension?.find(
      (el: any) =>
        el.url === "http://terminology.hl7.org/ValueSet/v3-EducationLevel"
    ),
    religionExist: patient?.extension?.find(
      (el: any) =>
        el.url === "http://terminology.hl7.org/ValueSet/v3-ReligiousAffiliation"
    ),
    maritalStatusExists: patient?.maritalStatus,
    occupationExists: patient?.extension?.find(
      (el: any) =>
        el.url === "https://terminologia.saude.gov.br/fhir/ValueSet/BROcupacao"
    ),
    familyIncomeExists: patient?.extension?.find(
      (el: any) =>
        el.url === "http://www.saude.gov.br/fhir/StructureDefinition/BRRendaFamiliar"
    ),
  };

  const auxAddress = patient?.address?.[0];
  const city = auxAddress?.city || "";
  const addressState = auxAddress?.state || "";
  const postalCode = auxAddress?.postalCode || "";
  const line = auxAddress?.line?.join(", ") || "";

  const address =
    [line, city, addressState, postalCode]
      ?.filter((el: any) => el)
      ?.join(" , ") || "-";

  const demographicData: DemographicDataType = {
    displayRace: {
      label: "Raça/Cor",
      display: auxType?.raceExists?.valueCodeableConcept?.coding?.[0]?.display,
    },
    displayGenderIdentity: {
      label: "Identidade de gênero",
      display:
        auxType?.genderExists?.valueCodeableConcept?.coding?.[0]?.display,
    },
    displayEducation: {
      label: "Escolaridade",
      display:
        auxType?.educationExists?.valueCodeableConcept?.coding?.[0]?.display,
    },
    displayReligion: {
      label: "Religião",
      display:
        auxType?.religionExist?.valueCodeableConcept?.coding?.[0]?.display,
    },
    displayMaritalStatus: {
      label: "Estado civil",
      display: auxType?.maritalStatusExists?.coding?.[0]?.display,
    },
    displayOccupation: {
      label: "Profissão",
      display:
        auxType?.occupationExists?.valueCodeableConcept?.coding?.[0]?.display,
    },
    displayFamilyIncome: {
      label: "Renda Familiar",
      display:
        auxType?.familyIncomeExists?.valueCodeableConcept?.coding?.[0]?.display,
    },
    displayAddress: {
      label: "Endereço",
      display: address || "-",
    },
  };

  const phone = patientPhone;

  const cpf = patient?.id?.split("-")?.[1];

  const socialName =
    patient?.name?.find((el: any) => el.use === "usual")?.text || "-";

  const data = [
    {
      label: "Nome",
      description: formatNameDisplay(patient?.name?.[0]?.text || "-"),
    },
    {
      label: "Nome social",
      description: formatNameDisplay(socialName),
    },
    {
      label: demographicData?.displayOccupation?.label,
      description: demographicData?.displayOccupation?.display || "-",
    },
    {
      label: "CPF",
      description: cpf,
    },
    {
      label: "Telefone",
      description: (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.25}
          sx={{ minWidth: 0, overflow: "hidden" }}
        >
          <Typography
            variant="body2"
            color="neutral1000.main"
            fontWeight={500}
            noWrap
            sx={{ textOverflow: "ellipsis", overflow: "hidden", minWidth: 0 }}
          >
            {useFormatter.formatPhoneNumber(phone || "")}
          </Typography>
          {whatsappPhone ? (
            <Tooltip title="Enviar WhatsApp">
              <IconButton
                size="small"
                color="primary"
                sx={{ flexShrink: 0, p: 0.25 }}
                onClick={handleOpenSendWhatsAppDialog}
              >
                <WhatsApp fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      ),
    },

    {
      label: "Sexo de nascimento",
      description: formatGender(patient?.gender) || "-",
    },
    {
      label: "Idade",
      description: patient?.birthDate
        ? `${formatAge(`${patient?.birthDate}`)} anos`
        : "-",
    },
    {
      label: "Data de nascimento",
      description: patient?.birthDate
        ? formatDate(`${patient?.birthDate}`)
        : "-",
    },
  ];

  const demographicsData = [
    {
      label: demographicData?.displayEducation?.label,
      description: demographicData?.displayEducation?.display || "-",
    },
    {
      label: demographicData?.displayReligion?.label,
      description: demographicData?.displayReligion?.display || "-",
    },
    {
      label: demographicData?.displayMaritalStatus?.label,
      description: demographicData?.displayMaritalStatus?.display || "-",
    },
    {
      label: demographicData?.displayFamilyIncome?.label,
      description: demographicData?.displayFamilyIncome?.display || "-",
    },
    {
      label: demographicData?.displayGenderIdentity?.label,
      description: demographicData?.displayGenderIdentity?.display || "-",
    },
    {
      label: demographicData?.displayAddress?.label,
      description: demographicData?.displayAddress?.display || "-",
    },
  ];

  // Lista única e ordenada de todos os campos do paciente.
  // A divisão em páginas é calculada dinamicamente pela largura disponível,
  // garantindo que cada página caiba em UMA única linha.
  const allFields = [
    data[0], // Nome
    data[1], // Nome social
    data[3], // CPF
    data[4], // Telefone
    data[5], // Sexo de nascimento
    data[6], // Idade
    data[7], // Data de nascimento
    data[2], // Profissão
    demographicsData[0], // Escolaridade
    demographicsData[2], // Estado civil
    demographicsData[1], // Religião
    demographicsData[3], // Renda Familiar
    demographicsData[4], // Identidade de gênero
    {
      label: demographicData?.displayRace?.label,
      description: demographicData?.displayRace?.display || "-",
    },
    demographicsData[5], // Endereço
  ];

  // Largura estimada por campo (inclui o gap). Divide a largura do container
  // por esse valor para saber quantos campos cabem em uma linha.
  const FIELD_WIDTH = 170;
  const FIELD_GAP = 16; // gap fixo (px) entre os campos, usado no cálculo da base
  const fieldsPerPage = Math.max(
    1,
    containerWidth > 0 ? Math.floor(containerWidth / FIELD_WIDTH) : 5
  );

  const totalPages = Math.max(1, Math.ceil(allFields.length / fieldsPerPage));

  // Garante que a página atual seja válida quando o total de páginas muda (zoom).
  const safeActiveStep = Math.min(activeStep, totalPages - 1);

  const currentFields = allFields.slice(
    safeActiveStep * fieldsPerPage,
    safeActiveStep * fieldsPerPage + fieldsPerPage
  );

  // Base fixa de cada coluna: divide a linha em exatamente fieldsPerPage colunas
  // (descontando os gaps). Assim os campos nunca ultrapassam 100% da largura e
  // mantêm o mesmo tamanho em todas as páginas, inclusive na última (menos itens).
  const fieldBasis = `calc((100% - ${
    (fieldsPerPage - 1) * FIELD_GAP
  }px) / ${fieldsPerPage})`;

  return (
    // Sem padding horizontal para alinhar com o RegisterArea (conteúdo em x=0).
    <Box sx={{ pt: 0.5 }}>
      {/* Card do cabeçalho: superfície arredondada e discreta para os dados do paciente */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          px: { xs: 1, md: 2 },
          py: { xs: 0.5, md: 0.75 },
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" alignItems="center" gap={{ xs: 0.5, md: 1 }}>
          {/* Navegação anterior */}
          <Tooltip title="Anterior">
            <span>
              <IconButton
                size="small"
                onClick={handleBack}
                disabled={safeActiveStep === 0}
                sx={{ flexShrink: 0 }}
              >
                <ChevronLeft
                  fontSize="small"
                  sx={{
                    color:
                      safeActiveStep === 0
                        ? "neutral700.main"
                        : "primary800.main",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>

          {/* Campos do paciente: sempre em UMA única linha (nowrap). Quando não
              cabem, o excedente vai para outra página (navegável pelas setas),
              conforme o cálculo dinâmico de fieldsPerPage pela largura. */}
          <Stack
            ref={fieldsContainerRef}
            direction="row"
            flexWrap="nowrap"
            justifyContent="flex-start"
            sx={{
              flex: 1,
              minWidth: 0,
              gap: `${FIELD_GAP}px`,
              overflow: "hidden",
            }}
          >
            {currentFields?.map((el, index) => {
              const fieldKey = `patient-field-${safeActiveStep}-${el.label}-${index}`;
              const isExpanded = expandedFieldKey === fieldKey;
              return (
              <Stack
                key={fieldKey}
                direction="column"
                spacing={0.25}
                sx={{
                  // grow=1 permite que as colunas ocupem o espaço livre da linha.
                  // Na última página (menos campos), o campo final — como o
                  // Endereço — se expande e aparece em uma única linha, sem quebrar.
                  flex: `1 1 ${fieldBasis}`,
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="caption"
                  color="neutral700.main"
                  fontWeight={600}
                  noWrap
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 0.2,
                    lineHeight: 1.5,
                    fontSize: 12.5,
                  }}
                >
                  {el.label}
                </Typography>
                {typeof el.description === "string" ? (
                  <Tooltip
                    title={isExpanded ? "" : el.description}
                    placement="bottom-start"
                  >
                    {/* Clicar no valor alterna entre truncado (...) e expandido
                        em várias linhas, útil para textos longos como o Endereço. */}
                    <Typography
                      variant="body2"
                      color="neutral1000.main"
                      fontWeight={500}
                      noWrap={!isExpanded}
                      onClick={() =>
                        setExpandedFieldKey(isExpanded ? null : fieldKey)
                      }
                      sx={{
                        cursor: "pointer",
                        overflow: "hidden",
                        ...(isExpanded
                          ? { whiteSpace: "normal", wordBreak: "break-word" }
                          : { textOverflow: "ellipsis" }),
                      }}
                    >
                      {el.description}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                    {el.description}
                  </Box>
                )}
              </Stack>
              );
            })}
          </Stack>

          {/* Navegação próxima */}
          <Tooltip title="Próximo">
            <span>
              <IconButton
                size="small"
                onClick={handleNext}
                disabled={safeActiveStep >= totalPages - 1}
                sx={{ flexShrink: 0 }}
              >
                <ChevronRight
                  fontSize="small"
                  sx={{
                    color:
                      safeActiveStep >= totalPages - 1
                        ? "neutral700.main"
                        : "primary800.main",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {messagingPatient ? (
        <SendWhatsAppMessageDialog
          open={isSendWhatsAppDialogOpen}
          onClose={() => setIsSendWhatsAppDialogOpen(false)}
          appointment={messagingAppointment}
          patient={messagingPatient}
          templates={messagingTemplates}
          organizationId={organizationId}
          practitionerId={`${oids.cpf}-${user?.username}`}
          taskPatientReference={taskPatientReference}
        />
      ) : null}
    </Box>
  );
}
